import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, computed, inject, input, signal, ViewChildren } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import {
  faArrowDownAZ,
  faArrowUpZA,
  faCodeBranch,
  faGripDotsVertical,
  faImage,
  faPlus,
  faStar,
  faThumbTack,
  faTrashCan
} from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';

import { of, startWith, switchMap } from 'rxjs';

import { Media } from '@eiq/files/models';

import { FileType } from '@empathy/global/data';
import { QuestionOption, QuestionType } from '@empathy/survey/base';

import { RemoveConditionalsRequest } from '../../../models/conditions/remove-conditionals-request.model';
import { reserveOptionId } from '../../../models/reserve-option-id.function';
import { SideOptionTabs } from '../../../models/side-option-tabs.enum';
import { IQuestionOptionForm } from '../../../models/type-forms/question-form-group.interface';
import { QuestionOptionForm } from '../../../models/type-forms/question-option-form.model';
import { SurveyConditionalsService } from '../../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { OptionsLibraryPickerComponent } from '../../options-library/options-library-picker/options-library-picker.component';
import { StimulusPickerDialogComponent } from '../../stimulus/stimulus-picker/stimulus-picker-dialog.component';
import { TextEditorComponent } from '../../text-editor/text-editor.component';

@Component({
  selector: 'empathy-choices-form',
  templateUrl: 'choices-form.component.html',
  styleUrls: ['./choices-form.component.scss', './_choices-form-theme.component.scss']
})
export class ChoicesFormComponent {
  @ViewChildren(TextEditorComponent) ckEditor: TextEditorComponent[];

  allowOther = input(true);
  allowNone = input(false);

  uploadingStimulus$ = signal<boolean>(false);
  surveyEditorService = inject(SurveyEditorService);

  addOptionAllowed$ = computed(() => {
    this.surveyEditorService.surveyEditor$();
    this.optionsChange$();

    if (this.options.length === 0) {
      return true;
    }

    const lastOption = this.options.controls[this.options.length - 1].value;
    return (lastOption?.text?.length ?? 0) > 0 || !!lastOption?.stimulus?.mediaId;
  });

  faArrowDownAZ = faArrowDownAZ;
  faArrowUpZA = faArrowUpZA;
  faCodeBranch = faCodeBranch;
  faGripDotsVertical = faGripDotsVertical;
  faImage = faImage;
  faPlus = faPlus;
  faStar = faStar;
  faStarSolid = faStarSolid;
  faThumbTack = faThumbTack;
  faTrashCan = faTrashCan;
  justAddedOptionId: string;
  questionType = QuestionType;

  private optionsChange$ = toSignal(
    toObservable(this.surveyEditorService.surveyEditor$).pipe(
      switchMap((editor) => {
        if (!editor) {
          return of([]);
        }
        return editor.questionActivated$.pipe(
          startWith(void 0),
          switchMap(() => editor.activeQuestionOptions.valueChanges.pipe(startWith(editor.activeQuestionOptions.value)))
        );
      })
    ),
    { initialValue: [] }
  );

  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private surveyConditionalsService = inject(SurveyConditionalsService);

  get options(): FormArray {
    return this.surveyEditorService.surveyEditor$().activeQuestionOptions;
  }

  onPaste(event: string[], pastedOnIndex: number): void {
    const currentOption = this.options.at(pastedOnIndex);
    const isCurrentEmpty = !currentOption.get('text').value;

    event.forEach((text, index) => {
      if (index === 0 && isCurrentEmpty) {
        currentOption.get('text').setValue(text);
      } else {
        const insertIndex = pastedOnIndex + index + (isCurrentEmpty ? 0 : 1);
        const { id, value } = this.nextOptionIdentity();
        const newOption = new QuestionOptionForm({ id, value, text } as QuestionOption);
        this.options.insert(insertIndex, newOption.form);
      }
    });

    this.cdr.detectChanges();
  }

  onAddOption(isLastOption: boolean, index: number = null): void {
    if (!isLastOption) {
      const option = this.options.value[index + 1];
      const editor: DecoupledEditor = this.ckEditor.find((ed) => ed.id === option.id).editorInstance;
      editor.editing.view.focus();
      return;
    }

    if (!this.addOptionAllowed$()) {
      return;
    }

    const newOption = new QuestionOptionForm();
    const newOptionId = this.nextOptionIdentity();

    newOption.form.get('id').setValue(newOptionId.id);
    newOption.form.get('value').setValue(`${newOptionId.value}`);
    this.justAddedOptionId = newOptionId.id;
    this.options.push(newOption.form);

    this.cdr.detectChanges();
  }

  onSetOther(active: boolean): void {
    if (active) {
      this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherInd').setValue(true);
      this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherPosition').setValue(this.options.length);
      return;
    }

    const affectedQuestionConditions = this.surveyConditionalsService.affectedQuestionConditionsByOptionRemoved('OTHER');
    const affectedHiddenQuestionConditions = this.surveyConditionalsService.affectedHiddenQuestionConditionsByOptionRemoved('OTHER');

    const affectedConditions = affectedQuestionConditions.concat(affectedHiddenQuestionConditions);

    if (affectedConditions.length === 0) {
      this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherInd').setValue(false);
      this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherPosition').setValue(-1);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Option',
        confirmText: `The option removed is used as a conditional setting on <strong>${affectedConditions.join(', ')}</strong>. If continue the condition/s will be removed.`,
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherInd').setValue(false);
        this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherPosition').setValue(-1);
        affectedQuestionConditions.forEach((qId) =>
          this.surveyConditionalsService.removeConditions(
            new RemoveConditionalsRequest(qId, [this.surveyEditorService.surveyEditor$().activeQuestion.get('id').value], 'OTHER')
          )
        );
        this.surveyConditionalsService.removeHiddenConditions(
          new RemoveConditionalsRequest(this.surveyEditorService.surveyEditor$().activeQuestion.get('id').value, affectedHiddenQuestionConditions, 'OTHER')
        );
      }
    });
  }

  onSetNone(active: boolean, activeQuestion: FormGroup): void {
    if (active) {
      activeQuestion.get('useNoneInd').setValue(true);
      return;
    }

    const affectedQuestionConditions = this.surveyConditionalsService.affectedQuestionConditionsByOptionRemoved('NONE');
    const affectedHiddenQuestionConditions = this.surveyConditionalsService.affectedHiddenQuestionConditionsByOptionRemoved('NONE');

    const affectedConditions = affectedQuestionConditions.concat(affectedHiddenQuestionConditions);

    if (affectedConditions.length === 0) {
      activeQuestion.get('useNoneInd').setValue(false);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Option',
        confirmText: `The option removed is used as a conditional setting on <strong>${affectedConditions.join(', ')}</strong>. If continue the condition/s will be removed.`,
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        activeQuestion.get('useNoneInd').setValue(false);
        affectedQuestionConditions.forEach((qId) =>
          this.surveyConditionalsService.removeConditions(new RemoveConditionalsRequest(qId, [activeQuestion.get('id').value], 'NONE'))
        );
        this.surveyConditionalsService.removeHiddenConditions(
          new RemoveConditionalsRequest(activeQuestion.get('id').value, affectedHiddenQuestionConditions, 'NONE')
        );
      }
    });
  }

  onOpenConditionals(option: AbstractControl): void {
    this.surveyEditorService.surveyEditor$().activeOption = option as FormGroup<IQuestionOptionForm>;
    this.surveyEditorService.activeTab$.set(SideOptionTabs.Conditions);
  }

  onRemoveAt(index: number, optionId: string): void {
    // TODO: Handle OTHER position

    const affectedQuestionConditions = this.surveyConditionalsService.affectedQuestionConditionsByOptionRemoved(optionId);
    const affectedHiddenQuestionConditions = this.surveyConditionalsService.affectedHiddenQuestionConditionsByOptionRemoved(optionId);

    if (affectedQuestionConditions.length === 0 && affectedHiddenQuestionConditions.length === 0) {
      this.options.removeAt(index);
      if (this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherPosition').value > this.options.length) {
        this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherPosition').setValue(this.options.length);
      }
      return;
    }

    const affectedConditions = affectedQuestionConditions.concat(affectedHiddenQuestionConditions);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Option',
        confirmText: `The option removed is used as a conditional setting on <strong>${affectedConditions.join(', ')}</strong>. If continue the condition/s will be removed.`,
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.surveyEditorService.removeOptionAt(index, optionId);
        if (this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherPosition').value > this.options.length) {
          this.surveyEditorService.surveyEditor$().activeQuestion.get('useOtherPosition').setValue(this.options.length);
        }
      }
    });
  }

  onSortOrder(order: 'asc' | 'desc'): void {
    const options = this.options.value;
    const factor = order === 'asc' ? 1 : -1;

    options.sort((a, b) => {
      const optionA = a.text.toLocaleUpperCase();
      const optionB = b.text.toLocaleUpperCase();

      return optionA.localeCompare(optionB) * factor;
    });

    this.options.patchValue(options);
  }

  drop(event: CdkDragDrop<string[]>): void {
    const activeQuestion = this.surveyEditorService.surveyEditor$().activeQuestion;
    if (event.previousIndex === activeQuestion.get('useOtherPosition').value) {
      activeQuestion.get('useOtherPosition').setValue(event.currentIndex);
      return;
    }

    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.options.controls, event.previousIndex, event.currentIndex);
    }
  }

  onAddStimulus(optionIndex: number): void {
    const dialogRef = this.dialog.open(StimulusPickerDialogComponent, {
      data: {
        types: [FileType.ImageJpeg, FileType.ImagePng, FileType.ImageGif]
      }
    });

    dialogRef.afterClosed().subscribe((files: Media[]) => {
      if (files) {
        this.options.at(optionIndex).get('stimulus').patchValue(files[0]);
      }
    });
  }

  onDeleteMedia(optionIndex: number): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Stimulus',
        confirmText: 'Are you sure you want to remove this stimulus?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.options.at(optionIndex).get('stimulus').reset();
      }
    });
  }

  onAddOptionsSet(): void {
    const dialogRef = this.dialog.open(OptionsLibraryPickerComponent);

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        if (res.optionsSet) {
          const replacedOptions = this.options.value;
          this.options.clear();

          res.optionsSet.options.forEach((option) => {
            const newOption = new QuestionOptionForm();
            newOption.form.get('id').setValue(this.nextOptionIdentity(replacedOptions).id);
            newOption.form.get('value').setValue(option.value);
            newOption.form.get('text').setValue(option.text);

            this.justAddedOptionId = newOption.form.get('id').value;
            this.options.push(newOption.form);
          });
        }
      }
    });
  }

  private nextOptionIdentity(replacedOptions: { id: string }[] = []): { id: string; value: string } {
    return reserveOptionId(this.surveyEditorService.surveyEditor$().activeQuestion.controls.lastOptionIndex, [...this.options.value, ...replacedOptions]);
  }
}
